import React from 'react';
import i18n from 'i18n-js';
import * as Yup from 'yup';
import { Formik, Field } from 'formik';

import Box from '@material-ui/core/Box';
import { useDispatch, useSelector } from 'react-redux';
import Grid from '@material-ui/core/Grid';
import { TextField } from 'components/FormFields';
import Container from '@material-ui/core/Container';
import Typography from '@material-ui/core/Typography';
import { userGroupsActions } from 'state/modules/userGroups';
import { MultipleVehicleGroupsSelect } from 'components/AsyncFields';

import useStyles from './DealerDetails.styled';

const DealerDetails = ({ formRef, errors, step, exit, type }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);

  const formSchema = Yup.object().shape({
    name: Yup.string().required(i18n.t('common.required_field')),
    state: Yup.string().required(i18n.t('common.required_field')),
    default_vehicle_group_id: Yup.array(Yup.object().shape({ id: Yup.number() })),
    role_default: Yup.string().required(i18n.t('common.required_field')),
    email: Yup.string().required(i18n.t('common.required_field')),
    zip_code: Yup.string().required(i18n.t('common.required_field')),
    address: Yup.string().required(i18n.t('common.required_field')),
    city: Yup.string().required(i18n.t('common.required_field')),
    phone_number: Yup.string(),
    user_group_type: Yup.string(),
    organization_id: Yup.string(),
  });

  const initialValues = {
    name: step?.name || '',
    default_vehicle_group_id: [],
    role_default: 'service_manager',
    user_group_type: 'business',
    city: '',
    state: '',
    email: '',
    address: '',
    zip_code: '',
    phone_number: '',
    organization_id: user?.organization?.id,
  };

  const onSubmit = (values) => {
    const payload = {
      ...values,
      default_vehicle_group_id: values.default_vehicle_group_id.map((vg) => vg.id),
    };

    // remove empty fields
    Object.keys(payload).forEach(
      (key) => key !== 'keyword' && !payload[key] && delete payload[key]
    );

    dispatch(userGroupsActions.fetchCreateUserGroup(payload, 'business', 'details', exit));
  };

  return (
    <Box className={classes.container}>
      <Container className="h-100">
        <Grid container direction="row" justifyContent="center" alignItems="center">
          <Grid item xs={11}>
            <Formik
              innerRef={formRef}
              initialValues={initialValues}
              validationSchema={formSchema}
              enableReinitialize={true}
              onSubmit={onSubmit}
            >
              {({ handleSubmit, values }) => (
                <form noValidate onSubmit={handleSubmit}>
                  <Grid
                    container
                    spacing={2}
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="flex-start"
                  >
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom>
                        <Box fontWeight="fontWeightBold" fontSize={14} mb={1}>
                          {i18n.t('user_groups.dealer_basic_info')}
                        </Box>
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Field
                        type="text"
                        name="name"
                        component={TextField}
                        asyncErrors={errors?.name}
                        label={i18n.t('common.name')}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Field
                        type="text"
                        name="address"
                        component={TextField}
                        asyncErrors={errors?.address}
                        label={i18n.t('common.address')}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Field
                        type="text"
                        name="state"
                        component={TextField}
                        asyncErrors={errors?.state}
                        label={i18n.t('common.state')}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Field
                        type="text"
                        name="email"
                        component={TextField}
                        asyncErrors={errors?.email}
                        label={i18n.t('fields.email')}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Field
                        type="text"
                        name="city"
                        component={TextField}
                        asyncErrors={errors?.city}
                        label={i18n.t('common.city')}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Field
                        type="text"
                        name="zip_code"
                        component={TextField}
                        asyncErrors={errors?.zip_code}
                        label={i18n.t('common.zip_code')}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Field
                        type="text"
                        name="phone_number"
                        component={TextField}
                        required={false}
                        asyncErrors={errors?.phone_number}
                        label={i18n.t('common.phone')}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom>
                        <Box fontWeight="fontWeightBold" fontSize={14} mb={1} mt={4}>
                          {i18n.t('common.vehicle_group')}
                        </Box>
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={10} md={6}>
                      <Field
                        name="default_vehicle_group_id"
                        component={MultipleVehicleGroupsSelect}
                        asyncErrors={errors?.default_vehicle_group_id}
                        defaultVehicleGroup={step?.default_vehicle_group}
                        label={i18n.t('user_groups.select_vehicle_group')}
                      />
                    </Grid>
                  </Grid>
                </form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default DealerDetails;
